<template>
  <button
    class="Btn"
    :class="classes"
    :type="type"
    @click="click"
    @keypress.enter="click">
    <Icon v-if="icon && !isLoading" :name="icon" />
    <template v-if="isLoading">
      <Loader />
    </template>
    <slot v-else />
  </button>
</template>

<script>
import Link from 'views/mixins/Link'
import { actions } from '../../utils'

export default {
  mixins: [Link],
  props: {
    disabled: {
      type: Boolean,
    },
    icon: {
      type: String,
    },
    iconSide: {
      default: 'left',
      type: String,
    },
    isLoading: {
      type: Boolean,
    },
    type: {
      default: 'button',
      type: String,
    },
  },
  computed: {
    classes() {
      return [
        this.disabled && 'disabled',
        this.isLoading && 'loading',
        this.icon && 'with-icon',
        this.icon && `icon-side-${this.iconSide}`,
      ]
    },
  },
  methods: {
    click(element) {
      if (this.isLoading || this.disabled) {
        element.preventDefault()
        return
      }

      this.go()
      this.$emit('update:isLoading', true)
      this.$emit('click')
    },
    ...actions(),
  },
}
</script>

<style lang="scss">
$_warning-text-color: mix($text-color, $info-dark, 70);

.Btn {
  @include trans;
  background: $main;
  border: 1px solid $main;
  border-radius: $radius;
  color: $white;
  cursor: pointer;
  display: block;
  font-size: $h6;
  font-weight: $semibold;
  letter-spacing: 1px;
  line-height: 1.75rem;
  margin: 0;
  outline: 0;
  padding: 0.5rem 1.5rem;
  position: relative;
  text-align: center;
  width: 100%;

  // link hover fix
  &[href]:hover,
  &[href]:focus {
    color: $white;
  }

  // state
  &.loading {
    cursor: default;
  }

  // with icon
  &.with-icon {
    padding-right: 1.25rem;

    &.loading {
      padding-right: 1rem;
    }
  }

  &.icon-side-left {
    padding-left: 0.75rem;

    > .Icon {
      float: left;
      margin-right: 0.5rem;
    }
  }

  &.icon-side-right {
    padding-right: 1rem;

    > .Icon {
      float: right;
      margin-left: 0.5rem;
      margin-right: -0.5rem;
      padding-left: 0.5rem;
    }
  }

  // --- theme
  &.light {
    background: $main-lighter;
    border: 1px solid $main-light;
    color: $main;

    &:hover {
      background: $white;
      border-color: $main-light;
      color: $main;
    }
  }

  &.inv {
    background: transparent;
    border-color: $main;
    color: $main;

    &:hover {
      background: $main-lighter;
      color: $main;
    }
  }

  &.link {
    background: transparent;
    border: none;
    border-bottom: 1px solid $link-color;
    border-radius: 0;
    color: $link-color;
    display: inline-block;
    padding: 0;
    width: auto;

    &.db {
      border-bottom: none;
    }

    &:hover {
      background: $main-lighter;
      color: $link-color-hover;
    }
  }

  &.success {
    background: $success;
    border-color: $success;
    color: $white;

    &.inv {
      background: transparent;
      color: $success;

      &:hover {
        background: $success;
        border-color: $success;
        color: $white;
      }
    }

    &.light {
      background: $success-lighter;
      border-color: $success-light;
      color: $success;

      &:hover {
        background: $success;
        border-color: $success;
        color: $white;
      }
    }

    &:hover {
      background: $success-light;
      border-color: $success-light;
    }
  }

  &.info {
    background: $info;
    border-color: $info;
    color: $white;

    &.inv {
      background: transparent;
      color: $info;

      &:hover {
        background: $info-lighter;
      }
    }

    &:hover {
      background: $info-light;
      border-color: $info-light;
    }
  }

  &.neutral {
    background: $neutral;
    border-color: $neutral;

    &.inv {
      background: transparent;
      color: $neutral;
    }
  }

  &.warning {
    background: $warning;
    border-color: $warning;
    color: $_warning-text-color;

    &.inv {
      background: transparent;
      color: $warning;
    }

    &:hover {
      background: $warning-light;
      border-color: $warning-light;
      color: $_warning-text-color;
    }
  }

  &.danger {
    background: $danger;
    border-color: $danger;
    color: $white;

    &.inv {
      background: transparent;
      color: $danger;

      &:hover {
        background: $danger-lighter;
        border-color: $danger-light;
      }
    }

    &.light {
      background: $danger-lighter;
      border-color: $danger-light;
      color: $danger;

      &:hover {
        background: $danger;
        border-color: $danger;
        color: $white;
      }
    }

    &:hover {
      background: $danger-light;
      border-color: $danger-light;
    }
  }

  &.disabled {
    background: $neutral;
    border-color: $border-color;
    color: $text-color;
    cursor: not-allowed;

    &.inv {
      background: transparent;
      border-color: $text-color-mid;
      color: $text-color-mid;
    }

    &:hover {
      background: $neutral;
      border-color: $border-color;
      color: $text-color;
    }
  }

  // --- size
  &.sm {
    font-size: $h7;
    padding: 0 1rem;
  }

  &.xs {
    font-size: $h8;
    line-height: 1.25rem;
    padding: 0 0.75rem;

    &.with-icon {
      padding: 0.25rem 1rem 0.25rem 0.5rem;
    }
  }

  &.lg {
    font-size: $h4;
    line-height: $lh * 1.5;
    padding: 0.5rem 2rem;
  }
  // &.xl {
  //   font-size: $h3;
  //   line-height: $lh * 2;
  //   padding: 1rem 4rem;
  // }

  // --- display
  &.ib {
    display: inline-block;
    width: auto;
  }

  &.no-border {
    background: transparent;
    border-width: 0;
  }

  &.db {
    display: block;
  }

  &:hover {
    background-color: $main-light;
    border-color: $main-light;
    color: $white;
  }
}
</style>
